<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-2" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
      <div class="col-6 col-md-2 col-lg-2">
        <select 
          class="form-select form-select-sm" 
          :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
          v-model="localFilters.class_id" 
          @change="applyFilters"
        >
          <option value="">{{ $t('students.filters.allClasses') }}</option>
          <option v-for="classItem in classes" :value="classItem.id" :key="classItem.id">
            {{ classItem.name }}
          </option>
        </select>
      </div>
      
      <div class="col-6 col-md-1 col-lg-1">
        <select 
          class="form-select form-select-sm" 
          :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
          v-model="localFilters.gender" 
          @change="applyFilters"
        >
          <option value="">{{ $t('students.filters.allGenders') }}</option>
          <option value="M">{{ $t('students.filters.males') }}</option>
          <option value="F">{{ $t('students.filters.females') }}</option>
        </select>
      </div>
       <!-- Add academic year filter -->
      <div class="col-6 col-md-2 col-lg-2">
        <select 
          class="form-select form-select-sm" 
          :class="locale === 'ar' ? 'form-select-rtl' : 'form-select-ltr'"
          v-model="localFilters.academic_year_id" 
          @change="applyFilters"
        >
          <option value="">{{ $t('students.all_academic_years') }}</option>
          <option v-for="year in academicYears" :value="year.id" :key="year.id">
            {{ year.name }} ({{ year.year }})
          </option>
        </select>
      </div>
      <div class="col-md-1 d-none d-md-block">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>
      
      <div class="col-6 col-md-3 col-lg-3 position-relative">
        <input
          type="text"
          class="form-control form-control-sm"
          :class="locale === 'ar' ? 'ps-5 text-end' : 'pe-5 text-start'"
          :placeholder="$t('students.filters.searchPlaceholder')"
          :value="localFilters.search"
          @input="handleSearchInput"
          :dir="locale === 'ar' ? 'rtl' : 'ltr'"
        />
        <span class="position-absolute top-50 translate-middle-y" :class="locale === 'ar' ? 'start-0 ps-3' : 'end-0 pe-3'">
          <i class="fas fa-search"></i>
        </span>
      </div>
      
      <div class="col-12 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ $t('students.filters.reset') }}
        </button>
      </div>
      
      <div class="col-12 col-md-3 col-lg-3">
        <button class="btn btn-secondary btn-sm w-100" @click="navigateToAddStudent" v-can="'students.create'">
          <i class="fas fa-plus"></i>
          {{ $t('students.filters.addNew') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onUnmounted,onMounted } from 'vue';
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import api from '@/api';

const { locale } = useI18n();
const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      search: '',
      gender: '',
      class_id: '',
      nationalNum: ''
    })
  },
  classes: {
    type: Array,
    default: () => []
  }
});

const academicYears = ref([]);
const router = useRouter();
const emit = defineEmits(['update:filters', 'apply-filters', 'reset-filters']);
const localFilters = ref({
  search: '',
  gender: '',
  class_id: '',
  nationalNum: '',
  academic_year_id: '',
});
let debounceTimeout = null;

const debounce = (fn, delay) => {
  return (...args) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    
    debounceTimeout = setTimeout(() => {
      fn(...args);
      debounceTimeout = null;
    }, delay);
  };
};

onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
});

const debouncedSearch = debounce((searchValue) => {
  localFilters.value.search = searchValue;
  applyFilters();
}, 300);

const handleSearchInput = (event) => {
  debouncedSearch(event.target.value);
};

const applyFilters = () => {
  emit('update:filters', localFilters.value);
  emit('apply-filters', localFilters.value);
};

const resetFilters = () => {
  localFilters.value = {
    search: '',
    gender: '',
    class_id: '',
    nationalNum: ''
  };
  emit('update:filters', localFilters.value);
  emit('reset-filters');
};

const navigateToAddStudent = () => {
  router.push('/students/add');
};
const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
  } catch (error) {
    console.error('Error fetching academic years:', error);
  }
};

onMounted(() => {
  fetchAcademicYears();
});

watch(() => props.filters, (newVal) => {
  localFilters.value = { ...newVal };
}, { deep: true });
</script>